import { Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { MainContainer } from "../../components"
import { usePost } from "../../api/reactQueryCustomHooks"
import { apiRoute, linksUrl } from "../../constants/enum"
import { useDispatch, useSelector } from "react-redux"
import { calculateTotalByKey, formatCurrency, selectFilter } from "../../Utility/utility"
import ExcelJS from 'exceljs';
import { saveAs } from "file-saver";
import { parse } from "@fortawesome/fontawesome-svg-core"
import { useNavigate, } from "react-router-dom"
import { setDetailLotno } from "../../state/stockEntrySlice"
import { toast } from "react-toastify"
import jsPDF from "jspdf"
import { autoTable } from "jspdf-autotable"
import { Checkbox, Select, SelectItem } from "@heroui/react"


const LotWisePositionReport = () => {
    const { compCode, compName } = useSelector((state) => state.company);

    const [isStkAtWise, setIsStkAtWise] = useState(false);
    const [selLotType, setSelLotType] = useState("ACTIVE");
    const [reportType, setReportType] = useState("BOTH");
    const [selectedLots, setSelectedLots] = useState([]);
    const dispatch = useDispatch()
    const navigate = useNavigate()

    /* ===================== STYLES ===================== */
    const tableClass = "table-auto  border-separate border-spacing-0   text-sm min-w-max";
    const headerCell = "border border-gray-700 px-2 py-1 text-center uppercase  font-bold";
    const subHeaderCell = "border border-gray-700 px-2 py-2 font-semibold text-sm text-center bg-gray-100";
    const cellClass = "border-1 border-gray-300 text-center p-1";
    const StickyLeftCell = "sticky text-center left-0 z-20 border-r-2 border-1 border-slate-700 bg-gradient-to-r from-orange-100 to-orange-50";

    /* ===================== FETCH DATA ===================== */
    const { data = [], mutate: filterData } = usePost({
        url: apiRoute.stockEntry.fetchLotWiseReport,
        queryKey: "lotwiseDatastck",
    });

    useEffect(() => {
        filterData({ data: { comp_code: compCode.join(","), trade_of: "P" } });
    }, []);
    const colColorClass = (col, type) => {

        if (["InwardMix", "InwardSng", "Total Inw"].includes(col))
            return type == "text" ? "text-cyan-700" : " bg-cyan-50";

        if (["SaleMix", "SaleSng", "Total Sale"].includes(col))
            return type == "text" ? "text-red-700" : "bg-red-50";

        if (["MemoMix", "MemoSng"].includes(col))
            return type == "text" ? "text-yellow-700" : "bg-yellow-50";

        if (["MixStock", "SingleStock"].includes(col))
            return type == "text" ? "text-green-700" : "bg-green-50";
        if (["TotalStock"].includes(col))
            return type == "text" ? "text-orange-700" : "bg-orange-100";
        if (type == "text" && !["TotalStock", "MixStock", "SingleStock", "MemoMix", "MemoSng", "InwardMix", "InwardSng", "Total Inw", "SaleMix", "SaleSng", "Total Sale"].includes(col))
            return "text-green-700"
        return "bg-white";
    };

    /* ===================== COLUMN CONFIG ===================== */
    const fixedStkAtTypes = ["InwardMix", "InwardSng", "SaleMix", "SaleSng", "MemoMix", "MemoSng"];
    const fixedStkAtHeader = {
        InwardMix: "Inward Mix",
        InwardSng: "Inward Single",
        SaleMix: "Sale Mix",
        SaleSng: "Sale Single",
        "Total Inw": "Total Inward",
        "Total Sale": "Total Sale",
        MemoMix: "Memo Mix",
        MemoSng: "Memo Single",
        "MixStock": "Mix Stock",
        "TotalStock": "Total Stock",
        "SingleStock": "Single Stock",
    };

    /* ===================== VISIBLE COLUMNS ===================== */
    const visibleFixedCols = useMemo(() => {
        if (reportType === "MIX") return ["InwardMix", "SaleMix", "MemoMix"];
        if (reportType === "SNG") return ["InwardSng", "SaleSng", "MemoSng"];
        return fixedStkAtTypes; // BOTH
    }, [reportType]);

    const showMixStock = reportType === "MIX" || reportType === "BOTH";
    const showDynamicStock = reportType !== "MIX";
    const showTotalStock = reportType !== "MIX";
    const showTotalsColumns = reportType === "BOTH";

    /* ===================== GROUP DATA ===================== */
    const filteredByRptType = useMemo(() => {
        if (reportType === "SNG") {
            return data.filter(item => item.PktType === "SNG");
        }
        if (reportType === "MIX") {
            return data.filter(item => item.PktType === "MIX");
        }
        return data; // BOTH → no filter
    }, [data, reportType]);
    const groupedData = useMemo(() => {
        const grouped = {};

        filteredByRptType.forEach((item) => {
            const lcode = item.L_Code || "N/A";
            const stk = item.StkAt;
            if (!grouped[lcode]) grouped[lcode] = {};
            if (!grouped[lcode][stk]) grouped[lcode][stk] = { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 };
            grouped[lcode][stk].Nos += Number(item.Nos || 0);
            grouped[lcode][stk].Weight += parseFloat(item.Weight || 0);
            grouped[lcode][stk].Rate += parseFloat(item.Rate || 0);
            grouped[lcode][stk].S_Rate += parseFloat(item.S_Rate || 0);
        });

        Object.keys(grouped).forEach((lcode) => {
            Object.keys(grouped[lcode]).forEach((stk) => {
                grouped[lcode][stk].Weight = Number(grouped[lcode][stk].Weight.toFixed(2));
                grouped[lcode][stk].Rate = parseFloat(grouped[lcode][stk].Rate.toFixed(2));
                grouped[lcode][stk].S_Rate = parseFloat(grouped[lcode][stk].S_Rate.toFixed(2));
            });
        });
        
        return grouped;
    }, [filteredByRptType]);

    /* ===================== DYNAMIC STOCK ===================== */
    const dynamicStkAt = useMemo(() => {
        const set = new Set(filteredByRptType.map((d) => d.StkAt));
        return [...set].filter((s) => !fixedStkAtTypes.includes(s));
    }, [filteredByRptType]);

    const getValue = (stkData, stk) => stkData?.[stk] || { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 };

    const filteredLots = useMemo(() => {
        // Step 1: Filter the entries
        const filteredEntries = Object.entries(groupedData).filter(([lotCode, stkData]) => {
            const mixStockWeight =
                getValue(stkData, "InwardMix")?.Weight - getValue(stkData, "SaleMix")?.Weight || 0;

            const dynamicWeight = dynamicStkAt.reduce(
                (acc, stk) => acc + (getValue(stkData, stk)?.Weight || 0),
                0
            );

            const memoWeight = getValue(stkData, "MemoSng")?.Weight || 0;

            const totalStockWeight = mixStockWeight + dynamicWeight + memoWeight;

            if (selLotType === "ACTIVE") return totalStockWeight > 0;
            if (selLotType === "CLOSED") return totalStockWeight <= 0;

            return true; // ALL
        });


        // Step 2: Convert filtered entries into dynamic object format
        const result = filteredEntries.reduce((acc, [lotCode, stkData]) => {
            // Include all StkAt keys dynamically
            acc[lotCode] = {};
            Object.keys(stkData).forEach((stk) => {
                acc[lotCode][stk] = { ...stkData[stk] };
            });
            return acc;
        }, {});

        return result;
    }, [groupedData, selLotType]);
    
    /* ===================== GRAND TOTAL ===================== */

    const grandTotals = useMemo(() => {
        const totals = {};

        // Columns that appear
        const allCols = [
            ...visibleFixedCols,
            ...(showTotalsColumns ? ["Total Inw", "Total Sale"] : []),
            ...(showMixStock ? ["MixStock"] : []),
            ...(showDynamicStock ? (isStkAtWise ? dynamicStkAt : ["SingleStock"]) : []),
            ...(showTotalStock ? ["TotalStock"] : []),
        ];

        // Initialize
        allCols.forEach((k) => {
            totals[k] = { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 };
        });

        Object.values(filteredLots).forEach((stkData) => {

            // Fixed columns
            visibleFixedCols.forEach((stk) => {
                const v = getValue(stkData, stk);
                totals[stk].Nos += v.Nos;
                totals[stk].Weight += v.Weight;
                totals[stk].Rate += v.Rate;
                totals[stk].S_Rate += v.S_Rate;
            });

            // Total Inw
            if (showTotalsColumns) {
                const totalInw = ["InwardMix", "InwardSng"].reduce(
                    (a, s) => {
                        const v = getValue(stkData, s);
                        return {
                            Nos: a.Nos + v.Nos,
                            Weight: a.Weight + v.Weight,
                            Rate: a.Rate + v.Rate,
                            S_Rate: a.S_Rate + v.S_Rate,
                        };
                    },
                    { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 }
                );
                totals["Total Inw"].Nos += totalInw.Nos;
                totals["Total Inw"].Weight += totalInw.Weight;
                totals["Total Inw"].Rate += totalInw.Rate;
                totals["Total Inw"].S_Rate += totalInw.S_Rate;
            }

            // Total Sale
            if (showTotalsColumns) {
                const totalSale = ["SaleMix", "SaleSng"].reduce(
                    (a, s) => {
                        const v = getValue(stkData, s);
                        return {
                            Nos: a.Nos + v.Nos,
                            Weight: a.Weight + v.Weight,
                            Rate: a.Rate + v.Rate,
                            S_Rate: a.S_Rate + v.S_Rate,
                        };
                    },
                    { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 }
                );
                totals["Total Sale"].Nos += totalSale.Nos;
                totals["Total Sale"].Weight += totalSale.Weight;
                totals["Total Sale"].Rate += totalSale.Rate;
                totals["Total Sale"].S_Rate += totalSale.S_Rate;
            }
            // TotalStock

            // Mix Stock
            if (showMixStock) {

                const mixStock = {
                    Nos: getValue(stkData, "InwardMix").Nos - getValue(stkData, "SaleMix").Nos,
                    Weight: getValue(stkData, "InwardMix").Weight - getValue(stkData, "SaleMix").Weight,
                    Rate: getValue(stkData, "InwardMix").Rate - getValue(stkData, "SaleMix").Rate,
                    S_Rate: getValue(stkData, "InwardMix").S_Rate - getValue(stkData, "SaleMix").S_Rate,
                };
                totals["MixStock"].Nos += mixStock.Nos;
                totals["MixStock"].Weight += mixStock.Weight;
                totals["MixStock"].Rate += mixStock.Rate;
                totals["MixStock"].S_Rate += mixStock.S_Rate;

            }

            // Dynamic stock
            if (showDynamicStock) {
                if (isStkAtWise) {
                    dynamicStkAt.forEach((stk) => {
                        if (selLotType == "CLOSED") {
                            totals[stk].Nos = 0;
                            totals[stk].Weight = 0;
                            totals[stk].Rate = 0;
                            totals[stk].S_Rate = 0;
                        }
                        else {
                            const v = getValue(stkData, stk);
                            totals[stk].Nos += v.Nos;
                            totals[stk].Weight += v.Weight;
                            totals[stk].Rate += v.Rate;
                            totals[stk].S_Rate += v.S_Rate;
                        }
                    });
                } else {

                    const v = dynamicStkAt.reduce(
                        (acc, stk) => {
                            const val = getValue(stkData, stk);
                            acc.Nos += val.Nos;
                            acc.Weight += val.Weight;
                            acc.Rate += val.Rate;
                            acc.S_Rate += val.S_Rate;
                            return acc;
                        },
                        { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 }
                    );
                    totals["SingleStock"].Nos += v.Nos;
                    totals["SingleStock"].Weight += v.Weight;
                    totals["SingleStock"].Rate += v.Rate;
                    totals["SingleStock"].S_Rate += v.S_Rate;

                }
            }

            // TotalStock
            if (showTotalStock) {
                const totalStock = { ...totals["MixStock"] };
                const lotMixStock = showMixStock
                    ? {
                        Nos:
                            getValue(stkData, "InwardMix").Nos -
                            getValue(stkData, "SaleMix").Nos,
                        Weight:
                            getValue(stkData, "InwardMix").Weight -
                            getValue(stkData, "SaleMix").Weight,
                        Rate:
                            getValue(stkData, "InwardMix").Rate -
                            getValue(stkData, "SaleMix").Rate,
                        S_Rate:
                            getValue(stkData, "InwardMix").S_Rate -
                            getValue(stkData, "SaleMix").S_Rate,
                    }
                    : { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 };

                if (isStkAtWise) {
                    dynamicStkAt.forEach((stk) => {
                        const v = getValue(stkData, stk);
                        totalStock.Nos += v.Nos;
                        totalStock.Weight += v.Weight;
                        totalStock.Rate += v.Rate;
                        totalStock.S_Rate += v.S_Rate;
                    });
                } else {
                    totalStock.Nos += totals["SingleStock"].Nos;
                    totalStock.Weight += totals["SingleStock"].Weight;
                    totalStock.Rate += totals["SingleStock"].Rate;
                    totalStock.S_Rate += totals["SingleStock"].S_Rate;
                }
                const lotSaleStock = dynamicStkAt.reduce(
                    (acc, stk) => {
                        const v = getValue(stkData, stk);
                        acc.Nos += v.Nos;
                        acc.Weight += v.Weight;
                        acc.Rate += v.Rate;
                        acc.S_Rate += v.S_Rate;
                        return acc;
                    },
                    { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 }
                );
                const lotMemoSng = {
                    Nos: getValue(stkData, "MemoSng").Nos,
                    Weight: getValue(stkData, "MemoSng").Weight,
                    Rate: getValue(stkData, "MemoSng").Rate,
                    S_Rate: getValue(stkData, "MemoSng").S_Rate,
                };

                totals["TotalStock"].Nos += lotMixStock.Nos + lotSaleStock.Nos + lotMemoSng.Nos;
                totals["TotalStock"].Weight += lotMixStock.Weight + lotSaleStock.Weight + lotMemoSng.Weight;
                totals["TotalStock"].Rate += lotMixStock.Rate + lotSaleStock.Rate + lotMemoSng.Rate;
                totals["TotalStock"].S_Rate += lotMixStock.S_Rate + lotSaleStock.S_Rate + lotMemoSng.S_Rate;



            }
        });

        // Round off decimals
        Object.keys(totals).forEach((k) => {
            totals[k].Weight = Number(totals[k].Weight.toFixed(2));
            totals[k].Rate = Number(totals[k].Rate.toFixed(2));
            totals[k].S_Rate = Number(totals[k].S_Rate.toFixed(2));
        });

        return totals;
    }, [filteredLots, dynamicStkAt, selLotType, isStkAtWise, visibleFixedCols]);

    useEffect(() => {
        if (reportType == "" || reportType == null) {
            setReportType("BOTH")
        }
    }, [reportType])

    const handleLotSelect = (lotCode) => {
        setSelectedLots((prev) => {
            if (prev.includes(lotCode)) {
                // unselect
                return prev.filter(code => code !== lotCode);
            } else {
                // select
                return [...prev, lotCode];
            }
        });
    };
    const handleLotDetail = () => {
        if (selectedLots && selectedLots.length > 0) {
            dispatch(setDetailLotno(selectedLots))
            navigate(linksUrl.rptLotwiseDetail)
        }
        else {
            toast.error("select Lot No To Get Info", {
                "position": "top-center"
            })
        }
    }

    const fixColumn = ["Nos", "Weight", "Rate", "S_Rate"]
    const computeSpecialStocks = (stkData, dynamicStkAt, isStkAtWise) => {
        const mixStock = {
            Nos: getValue(stkData, "InwardMix").Nos - getValue(stkData, "SaleMix").Nos,
            Weight: getValue(stkData, "InwardMix").Weight - getValue(stkData, "SaleMix").Weight,
            Rate: getValue(stkData, "InwardMix").Rate - getValue(stkData, "SaleMix").Rate,
            S_Rate: getValue(stkData, "InwardMix").S_Rate - getValue(stkData, "SaleMix").S_Rate,
        };

        const dynamicStock = dynamicStkAt.reduce(
            (acc, stk) => {
                const v = getValue(stkData, stk);
                acc.Nos += v.Nos;
                acc.Weight += v.Weight;
                acc.Rate += v.Rate;
                acc.S_Rate += v.S_Rate;
                return acc;
            },
            { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 }
        );

        const singleStock = { ...dynamicStock };


        const totalStock = {
            Nos: mixStock.Nos + dynamicStock.Nos + getValue(stkData, "MemoSng").Nos,
            Weight: mixStock.Weight + dynamicStock.Weight + getValue(stkData, "MemoSng").Weight,
            Rate: mixStock.Rate + dynamicStock.Rate + getValue(stkData, "MemoSng").Rate,
            S_Rate: mixStock.S_Rate + dynamicStock.S_Rate + getValue(stkData, "MemoSng").S_Rate,
        };

        return { mixStock, dynamicStock, singleStock, totalStock };
    };

    const exportToExcel = async (groupedData, dynamicStkAt, reportType, isStkAtWise) => {
        let exportData = groupedData;

        if (selectedLots && selectedLots.length > 0) {
            exportData = Object.fromEntries(
                Object.entries(groupedData).filter(([key]) => selectedLots.includes(key))
            );
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('LotWisePositionReport');

        // --- 0️⃣ Add report title and date ---
        const company = compName.join(",");
        const reportTitle = reportType === "SNG"
            ? "SINGLE PACKET LOT WISE STOCK POSITION"
            : reportType === "MIX"
                ? "MIX PACKET LOT WISE STOCK POSITION"
                : "LOT WISE STOCK POSITION";
        const today = new Date().toLocaleDateString("en-GB");

        const titleRow = worksheet.addRow([company]);
        const dateRow = worksheet.addRow([`${reportTitle} - ( ${today} )`]);
        worksheet.addRow([]); // blank row

        const totalColsCount = 1; // placeholder, will calculate later

        // Merge title & date rows
        worksheet.mergeCells(titleRow.number, 1, titleRow.number, 30);
        worksheet.mergeCells(dateRow.number, 1, dateRow.number, 30);

        titleRow.font = { size: 14, bold: true };
        titleRow.alignment = { horizontal: 'center' };
        dateRow.font = { bold: true };
        dateRow.alignment = { horizontal: 'center' };

        // --- 1️⃣ Headers ---
        const fixedStkAtHeader = {
            InwardMix: "Inward Mix",
            InwardSng: "Inward Single",
            SaleMix: "Sale Mix",
            SaleSng: "Sale Single",
            MemoMix: "Memo Mix",
            MemoSng: "Memo Single",
        };
        const fixedStkAtTypes = ["InwardMix", "InwardSng", "SaleMix", "SaleSng", "MemoMix", "MemoSng"];
        const visibleFixedCols =
            reportType === "MIX"
                ? ["InwardMix", "SaleMix", "MemoMix"]
                : reportType === "SNG"
                    ? ["InwardSng", "SaleSng", "MemoSng"]
                    : fixedStkAtTypes;

        const subHeaderUnits = ["Pcs", "Weight", "Value(RS)", "Value($)"];

        // --- Build header rows ---
        const mainHeader = ["Lot No"];
        const mainHeaderNames = []; // Track the actual main header for each column
        const addHeader = (name) => { for (let i = 0; i < 4; i++) { mainHeader.push(name); mainHeaderNames.push(name); } };

        //const addHeader = (name, count = 4) => { for (let i = 0; i < count; i++) mainHeader.push(name); };

        visibleFixedCols.forEach(col => addHeader(fixedStkAtHeader[col]));
        if (reportType === "BOTH") { addHeader("Total Inw", 4); addHeader("Total Sale", 4); }
        if (reportType !== "SNG") addHeader("MixStock", 4);
        if (reportType !== "MIX") {
            if (isStkAtWise) dynamicStkAt.forEach(stk => addHeader(stk, 4));
            else addHeader("SingleStock", 4);
            addHeader("TotalStock", 4);
        }

        const subHeader = [""];
        const addSubHeader = (count) => { for (let i = 0; i < count; i++) subHeader.push(...subHeaderUnits); };
        addSubHeader(visibleFixedCols.length);
        if (reportType === "BOTH") addSubHeader(2);
        if (reportType !== "SNG") addSubHeader(1);
        if (reportType !== "MIX") {
            if (isStkAtWise) addSubHeader(dynamicStkAt.length);
            else addSubHeader(1);
            addSubHeader(1);
        }

        const headerRow = worksheet.addRow(mainHeader);
        const subHeaderRow = worksheet.addRow(subHeader);


        // --- 5️⃣ Merge main headers ---
        let colIndex = 2; // ExcelJS 1-based, Lot No is 1
        visibleFixedCols.forEach(() => { worksheet.mergeCells(4, colIndex, 4, colIndex + 3); colIndex += 4; });
        if (reportType === "BOTH") { worksheet.mergeCells(4, colIndex, 4, colIndex + 3); colIndex += 4; worksheet.mergeCells(4, colIndex, 4, colIndex + 3); colIndex += 4; }
        if (reportType !== "SNG") { worksheet.mergeCells(4, colIndex, 4, colIndex + 3); colIndex += 4; }
        if (reportType !== "MIX") {
            if (isStkAtWise) { dynamicStkAt.forEach(() => { worksheet.mergeCells(4, colIndex, 4, colIndex + 3); colIndex += 4; }); }
            else { worksheet.mergeCells(4, colIndex, 4, colIndex + 3); colIndex += 4; }
            worksheet.mergeCells(4, colIndex, 4, colIndex + 3); // TotalStock
        }
        const headerColors = {
            "Inward Mix": 'FFB0E0E6', // light blue
            "Inward Single": 'FFB0E0E6',
            "Sale Mix": 'FFC6EFCE',   // light green
            "Sale Single": 'FFC6EFCE',
            "Memo Mix": 'FFFFF2CC',   // light yellow
            "Memo Single": 'FFFFF2CC',
            "Total Inw": 'FFB0E0E6',  // gray
            "Total Sale": 'FFC6EFCE',
            "MixStock": 'FFFFC0CB',   // light pink
            "SingleStock": 'FFFFC0CB',
            "TotalStock": 'FFDBBB', // light sky blue
        };

        // --- 6️⃣ Apply colors & styles to headers and subheaders ---
        [4, 5].forEach((r, rowIdx) => {
            const row = worksheet.getRow(r);
            row.eachCell((cell, c) => {
                const mainHeaderName = mainHeaderNames[c - 2] || "Lot No"; // c is 1-based
                const fillColor = headerColors[mainHeaderName] || 'FFFFC0CB'; // default light gray
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
                cell.font = { bold: true };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
            });
        });


        // --- 2️⃣ Data rows ---
        const allZero = (colKey, k) => Object.values(exportData).every(d => {
            let val = 0;
            if (visibleFixedCols.includes(colKey)) val = getValue(d, colKey)[k];
            else if (colKey === "Total Inw") val = ["InwardMix", "InwardSng"].reduce((a, s) => a + getValue(d, s)[k], 0);
            else if (colKey === "Total Sale") val = ["SaleMix", "SaleSng"].reduce((a, s) => a + getValue(d, s)[k], 0);
            else if (colKey === "MixStock") val = getValue(d, "InwardMix")[k] - getValue(d, "SaleMix")[k];
            else if (colKey === "SingleStock") val = dynamicStkAt.reduce((a, stk) => a + getValue(d, stk)[k], 0);
            else if (dynamicStkAt.includes(colKey)) val = getValue(d, colKey)[k];
            else if (colKey === "TotalStock") {
                const mix = getValue(d, "InwardMix")[k] - getValue(d, "SaleMix")[k];
                const dyn = isStkAtWise ? dynamicStkAt.reduce((a, stk) => a + getValue(d, stk)[k], 0) : getValue(d, "SingleStock")[k];
                val = mix + dyn;
            }
            return val === 0;
        });

        Object.entries(exportData).forEach(([lcode, stkData]) => {
            const { mixStock, dynamicStock, singleStock, totalStock } = computeSpecialStocks(stkData, dynamicStkAt, isStkAtWise);
            const totalInw = ["InwardMix", "InwardSng"].reduce((a, s) => { const v = getValue(stkData, s); a.Nos += v.Nos; a.Weight += v.Weight; a.Rate += v.Rate; a.S_Rate += v.S_Rate; return a; }, { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 });
            const totalSale = ["SaleMix", "SaleSng"].reduce((a, s) => { const v = getValue(stkData, s); a.Nos += v.Nos; a.Weight += v.Weight; a.Rate += v.Rate; a.S_Rate += v.S_Rate; return a; }, { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 });

            const row = [lcode];
            visibleFixedCols.forEach(stk => {
                const v = getValue(stkData, stk);
                row.push(
                    allZero(stk, "Nos") ? "" : v.Nos,
                    allZero(stk, "Weight") ? "" : v.Weight.toFixed(2),
                    allZero(stk, "Rate") ? "" : v.Rate.toFixed(2),
                    allZero(stk, "S_Rate") ? "" : v.S_Rate.toFixed(2)
                );
            });
            if (reportType === "BOTH") {
                ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row.push(allZero("Total Inw", k) ? "" : k === "Nos" ? totalInw.Nos : totalInw[k].toFixed(2)));
                ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row.push(allZero("Total Sale", k) ? "" : k === "Nos" ? totalSale.Nos : totalSale[k].toFixed(2)));
            }
            if (reportType !== "SNG") ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row.push(allZero("MixStock", k) ? "" : k === "Nos" ? mixStock.Nos : mixStock[k].toFixed(2)));
            if (reportType !== "MIX") {
                if (isStkAtWise) {
                    dynamicStkAt.forEach(stk => { const v = getValue(stkData, stk);["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row.push(allZero(stk, k) ? "" : k === "Nos" ? v.Nos : v[k].toFixed(2))); });
                } else { ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row.push(allZero("SingleStock", k) ? "" : k === "Nos" ? singleStock.Nos : singleStock[k].toFixed(2))); }
                ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row.push(k === "Nos" ? totalStock.Nos : totalStock[k].toFixed(2)));
            }
            const excelRow = worksheet.addRow(row);
            // ✅ Apply header-like color, alignment, and border to data row
            excelRow.eachCell((cell, c) => {
                const mainHeaderName = mainHeaderNames[c - 2] || "Lot No"; // same logic as header
                const fillColor = headerColors[mainHeaderName] || 'FFFFC0CB'; // default if missing
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
                cell.font = { bold: false };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
            });
            // excelRow.eachCell(cell => {
            //     cell.alignment = { horizontal: 'center', vertical: 'middle' };
            //     cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            // });
        });

        // --- 3️⃣ Footer totals ---
        const footerRow = worksheet.addRow(["Total"]);
        for (let c = 2; c <= worksheet.columns.length; c++) {
            let sum = 0;
            worksheet.eachRow({ includeEmpty: false }, (r, rowNumber) => {
                if (rowNumber > headerRow.number + 1) {
                    const val = r.getCell(c).value;
                    sum += typeof val === 'number' ? val : parseFloat(val) || 0;
                }
            });
            footerRow.getCell(c).value = sum === 0 ? "" : sum;
        }
        footerRow.eachCell(cell => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6E6FA' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        // --- 4️⃣ Save file ---
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

        const timestamp = new Date().toISOString().replace(/[-:.]/g, "").slice(0, 15);
        saveAs(blob, `LotWisePositionReport_${timestamp}.xlsx`);
    };

    const exportToPDF = (groupedData, dynamicStkAt, reportType, isStkAtWise) => {

        let exportData = groupedData

        if (selectedLots && selectedLots.length > 0) {
            const filteredData = Object.fromEntries(
                Object.entries(groupedData).filter(([key]) =>
                    selectedLots.includes(key)
                )
            );
            exportData = filteredData
        }
        const doc = new jsPDF("l", "pt", "a4");
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(12);

        // --- 1️⃣ Report header ---
        const companyName = compName.join(","); // dynamic if needed
        const reportTitle =
            reportType === "SNG"
                ? "SINGLE PACKET LOT WISE STOCK POSITION"
                : reportType === "MIX"
                    ? "MIX PACKET LOT WISE STOCK POSITION"
                    : "LOT WISE STOCK POSITION";
        const today = new Date().toLocaleDateString("en-GB");

        // --- 2️⃣ Fixed stock headers ---
        const fixedStkAtHeader = {
            InwardMix: "Inward Mix",
            InwardSng: "Inward Single",
            SaleMix: "Sale Mix",
            SaleSng: "Sale Single",
            MemoMix: "Memo Mix",
            MemoSng: "Memo Single",
        };
        const fixedStkAtTypes = ["InwardMix", "InwardSng", "SaleMix", "SaleSng", "MemoMix", "MemoSng"];
        const visibleFixedCols =
            reportType === "MIX"
                ? ["InwardMix", "SaleMix", "MemoMix"]
                : reportType === "SNG"
                    ? ["InwardSng", "SaleSng", "MemoSng"]
                    : fixedStkAtTypes;

        const subCols = ["Pcs", "Weight", "Value(RS)", "Value($)"];

        // --- 3️⃣ Build multi-header rows ---
        const headerRow1 = [
            {
                content: "Lot No",
                rowSpan: 2,
                styles: { halign: "center", fillColor: [245, 245, 245], lineWidth: 0.6, lineColor: [0, 0, 0], fontStyle: "bold", valign: "middle" }
            }
        ];
        const headerRow2 = [];

        const addHeaderGroup = (title) => {
            headerRow1.push({
                content: title,
                colSpan: 4,
                styles: { halign: "center", fillColor: [245, 245, 245], lineWidth: 0.6, lineColor: [0, 0, 0], fontStyle: "bold", valign: "middle" },
            });
            subCols.forEach((s) => headerRow2.push({ content: s, styles: { halign: "center", lineWidth: 0.4, lineColor: [100, 100, 100] } }));
        };

        visibleFixedCols.forEach((stk) => addHeaderGroup(fixedStkAtHeader[stk]));
        if (reportType === "BOTH") { addHeaderGroup("Total Inw"); addHeaderGroup("Total Sale"); }
        if (reportType !== "SNG") addHeaderGroup("MixStock");
        if (reportType !== "MIX") {
            if (isStkAtWise) dynamicStkAt.forEach((stk) => addHeaderGroup(stk));
            else addHeaderGroup("SingleStock");
            addHeaderGroup("TotalStock");
        }

        // --- 4️⃣ Columns mapping ---
        const columns = [{ header: "Lot No", dataKey: "lot" }];
        const addCols = (key) => ["Nos", "Weight", "Rate", "S_Rate"].forEach((k) =>
            columns.push({ header: `${key}-${k}`, dataKey: `${key}-${k}` })
        );

        visibleFixedCols.forEach(addCols);
        if (reportType === "BOTH") { addCols("Total Inw"); addCols("Total Sale"); }
        if (reportType !== "SNG") addCols("MixStock");
        if (reportType !== "MIX") {
            if (isStkAtWise) dynamicStkAt.forEach(addCols);
            else addCols("SingleStock");
            addCols("TotalStock");
        }

        // --- 5️⃣ Build data rows with zero-check ---
        const data = Object.entries(exportData).map(([lcode, stkData]) => {
            const { mixStock, singleStock, totalStock } = computeSpecialStocks(stkData, dynamicStkAt, isStkAtWise);

            const totalInw = ["InwardMix", "InwardSng"].reduce((a, s) => {
                const v = getValue(stkData, s);
                a.Nos += v.Nos; a.Weight += v.Weight; a.Rate += v.Rate; a.S_Rate += v.S_Rate;
                return a;
            }, { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 });

            const totalSale = ["SaleMix", "SaleSng"].reduce((a, s) => {
                const v = getValue(stkData, s);
                a.Nos += v.Nos; a.Weight += v.Weight; a.Rate += v.Rate; a.S_Rate += v.S_Rate;
                return a;
            }, { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 });

            const row = { lot: lcode };

            // Helper to check if all values in column are zero
            const allZero = (colKey, k) => {
                return Object.values(exportData).every(d => {
                    let val = 0;
                    if (visibleFixedCols.includes(colKey)) val = getValue(d, colKey)[k];
                    else if (colKey === "Total Inw") val = ["InwardMix", "InwardSng"].reduce((a, s) => a + getValue(d, s)[k], 0);
                    else if (colKey === "Total Sale") val = ["SaleMix", "SaleSng"].reduce((a, s) => a + getValue(d, s)[k], 0);
                    else if (colKey === "MixStock") val = getValue(d, "InwardMix")[k] - getValue(d, "SaleMix")[k];
                    else if (colKey === "SingleStock") val = dynamicStkAt.reduce((a, stk) => a + getValue(d, stk)[k], 0);
                    else if (dynamicStkAt.includes(colKey)) val = getValue(d, colKey)[k];
                    else if (colKey === "TotalStock") {
                        const mix = getValue(d, "InwardMix")[k] - getValue(d, "SaleMix")[k];
                        const dyn = isStkAtWise ? dynamicStkAt.reduce((a, stk) => a + getValue(d, stk)[k], 0) : singleStock[k];
                        val = mix + dyn;
                    }
                    return val === 0;
                });
            };

            // Fixed columns
            visibleFixedCols.forEach((stk) => {
                const v = getValue(stkData, stk);
                ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row[`${stk}-${k}`] = allZero(stk, k) ? "" : k === "Nos" ? v.Nos : v[k].toFixed(2));
            });

            // Total Inw / Sale
            if (reportType === "BOTH") {
                ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row[`Total Inw-${k}`] = allZero("Total Inw", k) ? "" : k === "Nos" ? totalInw.Nos : totalInw[k].toFixed(2));
                ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row[`Total Sale-${k}`] = allZero("Total Sale", k) ? "" : k === "Nos" ? totalSale.Nos : totalSale[k].toFixed(2));
            }

            // MixStock
            if (reportType !== "SNG") ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row[`MixStock-${k}`] = allZero("MixStock", k) ? "" : k === "Nos" ? mixStock.Nos : mixStock[k].toFixed(2));

            // Dynamic or Single stock
            if (reportType !== "MIX") {
                if (isStkAtWise) {
                    dynamicStkAt.forEach((stk) => {
                        const val = getValue(stkData, stk);
                        ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row[`${stk}-${k}`] = allZero(stk, k) ? "" : k === "Nos" ? val.Nos : val[k].toFixed(2));
                    });
                } else {
                    ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row[`SingleStock-${k}`] = allZero("SingleStock", k) ? "" : k === "Nos" ? singleStock.Nos : singleStock[k].toFixed(2));
                }
                ["Nos", "Weight", "Rate", "S_Rate"].forEach(k => row[`TotalStock-${k}`] = allZero("TotalStock", k) ? "" : k === "Nos" ? totalStock.Nos : totalStock[k].toFixed(2));
            }

            return row;
        });

        // --- 6️⃣ Footer totals ---
        const footerRow = { lot: "Grand Total" };
        columns.forEach(col => { if (col.dataKey !== "lot") footerRow[col.dataKey] = 0; });
        data.forEach(row => {
            columns.forEach(col => {
                if (col.dataKey !== "lot") {
                    const val = row[col.dataKey];
                    footerRow[col.dataKey] += typeof val === "number" ? val : parseFloat(val) || 0;
                }
            });
        });
        Object.keys(footerRow).forEach(k => { if (k !== "lot") footerRow[k] = k.endsWith("Nos") ? footerRow[k] : footerRow[k].toFixed(2); });

        // --- 7️⃣ Generate PDF ---
        autoTable(doc, {
            head: [headerRow1, headerRow2],
            body: data,
            foot: [footerRow],
            columns,
            theme: "grid",
            startY: 40,
            styles: { fontSize: 7, cellPadding: 2, halign: "right", lineWidth: 0.3, lineColor: [0, 0, 0] },
            headStyles: { fontStyle: "bold", textColor: [0, 0, 0], halign: "center", valign: "middle", fillColor: [230, 230, 230] },
            footStyles: { fillColor: [220, 220, 220], fontStyle: "bold", textColor: [0, 0, 0] },
            didParseCell: (data) => {
                if (data.section === "head") data.cell.styles.lineWidth = data.row.index === 0 ? 0.6 : 0.4;
                if (data.column.dataKey === "lot") data.cell.styles.halign = "center";
            },
            didDrawPage: (data) => {
                data.settings.margin.top = 45;
                doc.setFont("helvetica", "bold");
                doc.setFontSize(14);
                doc.text(companyName, pageWidth / 2, 20, { align: "center" });

                doc.setFontSize(10);
                doc.setFont("helvetica", "bold");
                doc.text(reportTitle, pageWidth / 2, 35, { align: "center" });

                doc.setFontSize(9);
                doc.text(`Date : ${today}`, pageWidth - 15, 20, { align: "right" });

                const str = `Page ${doc.internal.getNumberOfPages()}`;
                doc.setFontSize(8);
                doc.text(str, pageWidth - 50, pageHeight - 10);
            }
        });
        const timestamp = new Date().toISOString().replace(/[-:.]/g, "").slice(0, 15);
        doc.save(`LotWisePositionReport_${timestamp}.pdf`);
    };

    /* ===================== RENDER ===================== */
    return (
        <MainContainer pageName="Lot Wise Position" info={[]} breadcrumbs={["Reports", "Lot Wise Position"]}>
            <div className="bg-white p-2 h-screen">
                <h2 className="text-orange-500  text-left font-semibold text-sm">Total Records : {Object.keys(filteredLots)?.length || 0}</h2>
                <div className="mb-2 flex flex-wrap sm:flex-nowrap md:flex-nowrap justify-between w-full items-center shadow p-2 gap-x-2 gap-y-2">

                    {/* Left buttons */}
                    <div className="flex flex-nowrap gap-x-2 gap-y-2 justify-start">
                        <button
                            onClick={handleLotDetail}
                            className="bg-blue-600 text-white px-3 whitespace-nowrap h-7 py-1 rounded text-sm"
                        >
                            Lot Wise Info
                        </button>
                        <button
                            onClick={() => exportToPDF(filteredLots, dynamicStkAt, reportType, isStkAtWise)}
                            className="bg-red-500 whitespace-nowrap text-white px-3 py-1 h-7 rounded text-sm"
                        >
                            Export PDF
                        </button>
                        <button
                            onClick={() => exportToExcel(filteredLots, dynamicStkAt, reportType, isStkAtWise)}
                            className="bg-green-600 text-white whitespace-nowrap h-7 px-3 py-1 rounded text-sm"
                        >
                            Export Excel
                        </button>
                    </div>

                    {/* Right controls */}
                    <div className="flex flex-nowrap items-center justify-end gap-y-1 gap-x-5">
                        <Select
                            selectedKeys={[selLotType]}
                            variant="flat"
                            color="warning"
                            size="sm"
                            label="Lot Type : "
                            labelPlacement="outside"
                            onChange={(e) => setSelLotType(e.target.value)}
                            classNames={{ label: "xs" }}
                            className="sm:w-40 md:w-40 w-28 border-1 text-sm font-semibold"
                        >
                            <SelectItem key="ALL">ALL</SelectItem>
                            <SelectItem key="ACTIVE">ACTIVE</SelectItem>
                            <SelectItem key="CLOSED">CLOSED</SelectItem>
                        </Select>

                        <Select
                            selectedKeys={[reportType]}
                            variant="flat"
                            color="danger"
                            size="sm"
                            label="Stock Type : "
                            labelPlacement="outside"
                            onChange={(e) => setReportType(e.target.value)}
                            classNames={{ label: "xs" }}
                            className="sm:w-40 md:w-40 w-28 border-1 text-sm font-semibold"
                        >
                            <SelectItem key="BOTH">BOTH</SelectItem>
                            <SelectItem key="MIX">MIX</SelectItem>
                            <SelectItem key="SNG">SINGLE</SelectItem>
                        </Select>

                        <Checkbox
                            value={isStkAtWise}
                            onValueChange={setIsStkAtWise}
                            color="success"
                            variant="flat"
                            classNames={{ label: "text-green-700 text-sm font-semibold" }}
                            className="py-2 px-2 mt-3 rounded-md bg-green-100 border-blue-200 text-rose-700 border-0"
                            label="Location Wise Stock"
                        >Location Wise Stock</Checkbox>
                    </div>
                </div>


                <div className=" border-1  border-slate-500 overflow-auto max-h-[60vh]">
                    <table className={tableClass}>
                         <thead className="sticky top-0 z-40 bg-gray-200 shadow-md">
                            <tr>
                                <th rowSpan={2} className={StickyLeftCell}>Lot No</th>

                                {visibleFixedCols.map((stk) => (
                                    <th key={stk} colSpan={4} className={`${headerCell} ${colColorClass(stk, "head")}`}>
                                        {fixedStkAtHeader[stk]}
                                    </th>
                                ))}

                                {showTotalsColumns && (
                                    <>
                                        <th colSpan={4} className={`${headerCell} ${colColorClass("Total Inw")}`}>Total Inw</th>
                                        <th colSpan={4} className={`${headerCell} ${colColorClass("Total Sale")}`}>Total Sale</th>
                                    </>
                                )}

                                {showMixStock && <th colSpan={4} className={`${headerCell} ${colColorClass("MixStock")}`}>Mix Stock</th>}

                                {showDynamicStock &&
                                    (isStkAtWise
                                        ? dynamicStkAt.map((stk) => (
                                            <th key={stk} colSpan={4} className={`${headerCell} ${colColorClass("SingleStock")}`}>
                                                {stk}
                                            </th>
                                        ))
                                        : <th colSpan={4} className={`${headerCell} ${colColorClass("SingleStock")}`}>Single Stock</th>
                                    )
                                }

                                {showTotalStock && <th colSpan={4} className={`${headerCell} ${colColorClass("TotalStock")}`}>Total Stock</th>}
                            </tr>

                            <tr>

                                {[
                                    ...visibleFixedCols,
                                    ...(showTotalsColumns ? ["Total Inw", "Total Sale"] : []),
                                    ...(showMixStock ? ["MixStock"] : []),
                                    ...(showDynamicStock ? (isStkAtWise ? dynamicStkAt : ["SingleStock"]) : []),
                                    ...(showTotalStock ? ["TotalStock"] : []),
                                ].flatMap((c) =>
                                    ["Pcs", "Weight", "Value (₹)", "Value ($)"].map((s) => (
                                        <th key={`${c}-${s}`} className={`${subHeaderCell}  bg-white`}>{s}</th>
                                    ))
                                )}
                            </tr>
                        </thead>

                        <tbody>
                            {(() => {
                                // --- 1️⃣ Precompute which columns are all zero ---
                                const columnAllZero = {};
                                const allCols = [
                                    ...visibleFixedCols,
                                    ...(showTotalsColumns ? ["Total Inw", "Total Sale"] : []),
                                    ...(showMixStock ? ["MixStock"] : []),
                                    ...(showDynamicStock
                                        ? isStkAtWise
                                            ? dynamicStkAt
                                            : ["SingleStock"]
                                        : []),
                                    ...(showTotalStock ? ["TotalStock"] : []),
                                ];

                                allCols.forEach(col => {
                                    //["Nos", "Weight", "Rate", "S_Rate"].forEach(k => {
                                    fixColumn.forEach(k => {
                                        columnAllZero[`${col}-${k}`] = Object.entries(filteredLots).every(([lcode, stkData]) => {
                                            let value = 0;

                                            if (visibleFixedCols.includes(col)) {
                                                value = getValue(stkData, col)[k];
                                            } else if (col === "Total Inw") {
                                                value = ["InwardMix", "InwardSng"].reduce((a, s) => a + getValue(stkData, s)[k], 0);
                                            } else if (col === "Total Sale") {
                                                value = ["SaleMix", "SaleSng"].reduce((a, s) => a + getValue(stkData, s)[k], 0);
                                            } else if (col === "MixStock") {
                                                value = getValue(stkData, "InwardMix")[k] - getValue(stkData, "SaleMix")[k];
                                            } else if (col === "SingleStock") {
                                                value = dynamicStkAt.reduce((a, stk) => a + getValue(stkData, stk)[k], 0);
                                            } else if (dynamicStkAt.includes(col)) {
                                                value = getValue(stkData, col)[k];
                                            } else if (col === "TotalStock") {
                                                const mixStock = {
                                                    Nos: getValue(stkData, "InwardMix").Nos - getValue(stkData, "SaleMix").Nos,
                                                    Weight: getValue(stkData, "InwardMix").Weight - getValue(stkData, "SaleMix").Weight,
                                                    Rate: getValue(stkData, "InwardMix").Rate - getValue(stkData, "SaleMix").Rate,
                                                    S_Rate: getValue(stkData, "InwardMix").S_Rate - getValue(stkData, "SaleMix").S_Rate,
                                                };

                                                const dynamicStockVal = isStkAtWise
                                                    ? dynamicStkAt.reduce((a, stk) => a + getValue(stkData, stk)[k], 0)
                                                    : dynamicStkAt.reduce((a, stk) => a + getValue(stkData, stk)[k], 0);

                                                value = mixStock[k] + dynamicStockVal;
                                            }

                                            return value === 0;
                                        });
                                    });
                                });

                                // --- 2️⃣ Helper to render a cell ---
                                const renderCell = (value, colKey) => {

                                    const parts = colKey.split("-");

                                    const result = parts.length > 1 ? parts[1] : "";

                                    return columnAllZero[colKey] ? "" : result == "Rate" ? formatCurrency(value) : typeof value === "number" ? (value % 1 === 0 ? value : value.toFixed(2)) : value;
                                }

                                // --- 3️⃣ Render rows ---
                                return (
                                    <>
                                        {Object.entries(filteredLots).map(([lcode, stkData]) => {
                                            const totalInw = ["InwardMix", "InwardSng"].reduce(
                                                (a, s) => {
                                                    const v = getValue(stkData, s);
                                                    return {
                                                        Nos: a.Nos + v.Nos, Weight: a.Weight + v.Weight
                                                        , Rate: a.Rate + v.Rate, S_Rate: a.S_Rate + v.S_Rate
                                                    };
                                                },
                                                { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 }
                                            );

                                            const totalSale = ["SaleMix", "SaleSng"].reduce(
                                                (a, s) => {
                                                    const v = getValue(stkData, s);
                                                    return { Nos: a.Nos + v.Nos, Weight: a.Weight + v.Weight, Rate: a.Rate + v.Rate, S_Rate: a.S_Rate + v.S_Rate };
                                                },
                                                { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 }
                                            );

                                            const mixStock = {
                                                Nos: getValue(stkData, "InwardMix").Nos - getValue(stkData, "SaleMix").Nos,
                                                Weight: getValue(stkData, "InwardMix").Weight - getValue(stkData, "SaleMix").Weight,
                                                Rate: getValue(stkData, "InwardMix").Rate - getValue(stkData, "SaleMix").Rate,
                                                S_Rate: getValue(stkData, "InwardMix").S_Rate - getValue(stkData, "SaleMix").S_Rate,
                                            };

                                            const dynamicStock = isStkAtWise
                                                ? dynamicStkAt.reduce((acc, stk) => {
                                                    const v = getValue(stkData, stk);
                                                    acc[stk] = v;
                                                    return acc;
                                                }, {})
                                                : dynamicStkAt.reduce(
                                                    (acc, stk) => {
                                                        const v = getValue(stkData, stk);
                                                        acc.Nos += v.Nos;
                                                        acc.Weight += v.Weight;
                                                        acc.Rate += v.Rate;
                                                        acc.S_Rate += v.S_Rate;
                                                        return acc;
                                                    },
                                                    { Nos: 0, Weight: 0, Rate: 0, S_Rate: 0 }
                                                );

                                            const totalStock = isStkAtWise
                                                ? dynamicStkAt.reduce(
                                                    (a, stk) => {
                                                        const v = getValue(stkData, stk);
                                                        a.Nos += v.Nos;
                                                        a.Weight += v.Weight;
                                                        a.Rate += v.Rate;
                                                        a.S_Rate += v.S_Rate;
                                                        return a;
                                                    },
                                                    { ...mixStock }
                                                )
                                                : {
                                                    Nos: mixStock.Nos + dynamicStock.Nos, Weight: mixStock.Weight + dynamicStock.Weight,
                                                    Rate: mixStock.Rate + dynamicStock.Rate, S_Rate: mixStock.S_Rate + dynamicStock.S_Rate
                                                };

                                            return (
                                                <tr key={lcode}>
                                                    <td className={StickyLeftCell}>
                                                        <div className="flex pl-2 gap-2">
                                                            <input
                                                                type="checkbox"
                                                                className="w-4 h-4 text-indigo-600 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 transition duration-150 cursor-pointer"
                                                                checked={selectedLots.includes(lcode)}
                                                                onChange={() => handleLotSelect(lcode)}
                                                            />
                                                            {lcode}
                                                        </div>
                                                    </td>

                                                    {visibleFixedCols.map(stk =>
                                                        // ["Nos", "Weight", "Rate", "S_Rate"].map(k => (
                                                        fixColumn.map(k => (
                                                            <td key={`${lcode}-${stk}-${k}`} className={`${cellClass} ${colColorClass(stk)}`}>
                                                                {renderCell(getValue(stkData, stk)[k], `${stk}-${k}`)}
                                                            </td>
                                                        ))
                                                    )}

                                                    {showTotalsColumns &&
                                                        ["Total Inw", "Total Sale"].flatMap(col =>
                                                            fixColumn.map(k => (
                                                                <td key={`${lcode}-${col}-${k}`} className={`${cellClass} ${colColorClass(col)}`}>
                                                                    {renderCell(col === "Total Inw" ? totalInw[k] : totalSale[k], `${col}-${k}`)}
                                                                </td>
                                                            ))
                                                        )}

                                                    {showMixStock &&
                                                        // ["Nos", "Weight", "Rate", "S_Rate"].map(k => (
                                                        fixColumn.map(k => (
                                                            <td key={`${lcode}-mix-${k}`} className={`${cellClass} ${colColorClass("MixStock")}`}>
                                                                {renderCell(mixStock[k], `MixStock-${k}`)}
                                                            </td>
                                                        ))}

                                                    {showDynamicStock &&
                                                        (isStkAtWise
                                                            ? dynamicStkAt.flatMap(stk =>
                                                                fixColumn.map(k => (
                                                                    <td key={`${lcode}-${stk}-${k}`} className={`${cellClass} ${colColorClass("SingleStock")}`}>
                                                                        {renderCell(dynamicStock[stk][k], `${stk}-${k}`)}
                                                                    </td>
                                                                ))
                                                            )
                                                            : fixColumn.map(k => (
                                                                <td key={`${lcode}-sale-${k}`} className={`${cellClass} ${colColorClass("SingleStock")}`}>
                                                                    {renderCell(dynamicStock[k], `SingleStock-${k}`)}
                                                                </td>
                                                            )))}

                                                    {showTotalStock &&
                                                        fixColumn.map(k => (
                                                            <td key={`${lcode}-total-${k}`} className={`${cellClass} ${colColorClass("TotalStock")}`}>
                                                                {renderCell(totalStock[k], `TotalStock-${k}`)}
                                                            </td>
                                                        ))}
                                                </tr>
                                            );
                                        })}

                                        {/* GRAND TOTAL */}
                                        <tr className="sticky bottom-0 h-12 z-20 bg-white font-bold shadow-[0_-4px_10px_rgba(0,0,0,0.12)]">
                                        {/* <tr className="font-bold h-12 bg-white sticky bottom-0 z-20"> */}
                                            <td className={StickyLeftCell}>Grand Total</td>
                                            {Object.keys(grandTotals).map(col =>
                                                ["Nos", "Weight", "Rate", "S_Rate"].map(k => (
                                                    <td key={`gt-${col}-${k}`} className={`${cellClass} ${colColorClass(col, "text")}`}>
                                                        {columnAllZero[`${col}-${k}`]
                                                            ? ""
                                                            : k === "Nos"
                                                                ? grandTotals[col]?.Nos ?? 0
                                                                : k === "Rate" ? formatCurrency((grandTotals[col]?.[k] ?? 0)) :
                                                                    (grandTotals[col]?.[k] ?? 0).toFixed(2)}
                                                    </td>
                                                ))
                                            )}
                                        </tr>
                                    </>
                                );
                            })()}
                        </tbody>

                    </table>
                </div>
            </div>
        </MainContainer>
    );
};

export default LotWisePositionReport
{/*  <input
                                                    type="checkbox"
                                                    className="
    w-4 h-4     mr-2         
    text-indigo-600       
    bg-white               
    border border-gray-300 
    rounded               
    focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1
    transition duration-150
    cursor-pointer"
                                                    checked={selectedLots.includes(row.L_Code)}
                                                    onChange={() => handleLotSelect(row.L_Code)}
                                                /> */}